/**
 * ohlcheader.ts — the per-pane `O H L C  +495.00 (+0.79%)` row, as pure functions.
 *
 * Spec of record: `docs/design/PANE_OHLC_HEADER_SPEC_2026-08-17.md`. Decisions implemented here,
 * not re-decided:
 *
 *   * the selected bar is the HOVERED one, else the LAST RENDERED bar — forming or not (§2);
 *   * a forming bar shows its O/H/L/C and SUPPRESSES the change figure to `—`, disclosed by a
 *     leading `● live` glyph (§3). O/H/L/C are raw state; a change is a comparison against a
 *     period that has not finished, so it would tick and reverse sign mid-bar;
 *   * the baseline is `bars[i - 1].close` for the SELECTED bar, hover included, and `i === 0`
 *     yields `—` rather than reaching outside the served window (§4).
 *
 * WHY A MODULE AND NOT `main.ts`: `main.ts` calls `createChart` and `localStorage` at import time,
 * so vitest cannot import it (same reason `inspector.ts` exists). These three decisions are exactly
 * the ones that must be witnessed by a test rather than by a browser. No DOM, no chart import.
 */

import { fmtPx, fmtSigned, pxDp } from './tradepanel';
import type { Bar } from './chartpane';

/** Absent, per field. Never `0`, `NaN` or a blank — the row's shape must not jump. */
const ABSENT = '\u2014';

/** The selected bar and the bar the change is measured against. */
export type HeaderSelection = {
  /** null only when `bars` is empty. */
  bar: Bar | null;
  /** The predecessor's close, or null at `i === 0` (and when there is no bar). */
  prevClose: number | null;
  /** Is the selected bar the last one AND has it not closed? */
  forming: boolean;
};

export type HeaderFields = {
  o: string; h: string; l: string; c: string;
  /** `+495.00 (+0.79%)`, or `—` when forming / first bar / no bars. */
  change: string;
  forming: boolean;
  /** Colour carrier for the change fields only. `none` = no colour (forming, absent, or zero). */
  sign: 'up' | 'down' | 'none';
};

/**
 * Index of the bar nearest `time`, or -1 when there are none.
 *
 * Nearest rather than exact because the cursor sits BETWEEN bars for most of its travel. Strict
 * `<` so an exact tie keeps the EARLIER bar and the answer is deterministic, the same rule
 * `snapToBar` uses for a tie in O/H/L/C order.
 */
function nearestIndex(bars: readonly Bar[], time: number): number {
  if (!bars.length) return -1;
  let best = 0;
  let bestD = Math.abs(bars[0].time - time);
  for (let i = 1; i < bars.length; i++) {
    const d = Math.abs(bars[i].time - time);
    if (d < bestD) { best = i; bestD = d; }
  }
  return best;
}

/** Which bar the header speaks about, and what it is measured against. */
export function selectHeaderBar(
  bars: readonly Bar[],
  hoveredTime: number | null,
  lastBarForming: boolean,
): HeaderSelection {
  if (!bars.length) return { bar: null, prevClose: null, forming: false };
  const i = hoveredTime === null ? bars.length - 1 : nearestIndex(bars, hoveredTime);
  return {
    bar: bars[i],
    prevClose: i > 0 ? bars[i - 1].close : null,
    // Only the LAST bar can be forming, so hovering history shows a closed bar with its full
    // change figure even while the right edge is live.
    forming: lastBarForming && i === bars.length - 1,
  };
}

/** The field strings. `fmtPx`/`fmtSigned` already return `—` for null/non-finite. */
export function formatHeader(sel: HeaderSelection): HeaderFields {
  const b = sel.bar;
  const base: HeaderFields = {
    o: fmtPx(b?.open), h: fmtPx(b?.high), l: fmtPx(b?.low), c: fmtPx(b?.close),
    change: ABSENT, forming: sel.forming, sign: 'none',
  };
  // Forming, first bar of the window, or a zero baseline: absence is the honest answer.
  if (b === null || sel.forming || sel.prevClose === null || sel.prevClose === 0) return base;
  const abs = b.close - sel.prevClose;
  const pct = (abs / sel.prevClose) * 100;
  const pctS = `${abs > 0 ? '+' : abs < 0 ? '-' : ''}${Math.abs(pct).toFixed(2)}%`;
  return {
    ...base,
    // Decimals from the BASELINE's magnitude, via the same ladder `fmtPx` uses for the prices on
    // this row — a fixed 2dp printed `+0.00` beside a five-significant-digit price and a correct
    // `+4.55%` for 14 of 39 cached assets (every ratio pair included). A delta must never carry
    // fewer digits than the prices it is a delta of.
    change: `${fmtSigned(abs, pxDp(sel.prevClose), false)} (${pctS})`,
    sign: abs > 0 ? 'up' : abs < 0 ? 'down' : 'none',
  };
}
